import { useRef, type ReactNode } from "react";
import { motion, type AnimationDefinition } from "framer-motion";
import { collapse } from "./variants";

/**
 * In-flow accordion wrapper for toggleable cards (import wizard, edit panels,
 * "new" forms). Grows/shrinks its own measured height so the content that
 * follows glides instead of snapping when the card mounts/unmounts inside an
 * <AnimatePresence>. Keep padding and margins on the child, not on this
 * wrapper — border-box height can't collapse below an element's own padding.
 *
 * onShowComplete fires once the expand animation has fully settled — the
 * right moment to e.g. scroll the revealed card into view (its height and
 * position don't exist yet at mount time).
 */
export function CollapsePanel({
  className, children, onShowComplete,
}: {
  className?: string;
  children: ReactNode;
  onShowComplete?: (panel: HTMLElement | null) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const handleAnimationComplete = (definition: AnimationDefinition) => {
    if (definition === "show") onShowComplete?.(ref.current);
  };
  return (
    <motion.div ref={ref} className={className} variants={collapse} initial="hidden" animate="show" exit="exit" onAnimationComplete={handleAnimationComplete}>
      {children}
    </motion.div>
  );
}